import { api } from "@/api/client";
import { uploadFileChunked } from "@/lib/upload";
import { idbDel, idbGet, idbPut } from "@/lib/idb";

/** Coda di invio video: l'addetta può completare la pulizia subito, il video
 * viene inviato in background, ritentato e ripreso anche dopo aver chiuso l'app. */
const KEY = "video_queue_v1";
const listeners = new Set();
let running = false;

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const write = (jobs) => {
  localStorage.setItem(KEY, JSON.stringify(jobs));
  listeners.forEach((fn) => fn(jobs));
};

export const subscribeQueue = (fn) => {
  listeners.add(fn);
  fn(read());
  return () => listeners.delete(fn);
};

export const queueFor = (cleaningId, unitId = null) =>
  read().find((j) => j.kind !== "proof" && j.cleaningId === cleaningId && (j.unitId || null) === unitId) || null;

export const queueForCleaning = (cleaningId) => read().filter((j) => j.cleaningId === cleaningId);

export const proofJobFor = (cleaningId, reqId) =>
  read().find((j) => j.kind === "proof" && j.cleaningId === cleaningId && j.reqId === reqId) || null;

export async function enqueueVideo(cleaningId, file, unitId = null) {
  const id = `${cleaningId}:${unitId || "main"}:${Date.now()}`;
  await idbPut(id, file);
  const jobs = read().filter((j) => !(j.cleaningId === cleaningId && (j.unitId || null) === unitId));
  jobs.push({
    id,
    cleaningId,
    unitId: unitId || null,
    name: file.name,
    size: file.size,
    type: file.type || "video/mp4",
    lastModified: file.lastModified || 0,
    pct: 0,
    status: "pending",
    error: "",
  });
  write(jobs);
  processQueue();
  return id;
}

export async function enqueueProof(cleaningId, file, reqId, unitId = null, kind = "foto") {
  const id = `proof:${cleaningId}:${reqId}:${Date.now()}`;
  await idbPut(id, file);
  const jobs = read().filter((j) => !(j.cleaningId === cleaningId && j.reqId === reqId));
  jobs.push({
    id,
    kind: "proof",
    proofKind: kind,
    reqId,
    cleaningId,
    unitId: unitId || null,
    name: file.name,
    size: file.size,
    type: file.type || "image/jpeg",
    lastModified: file.lastModified || 0,
    pct: 0,
    status: "pending",
    error: "",
  });
  write(jobs);
  processQueue();
  return id;
}

export function removeJob(id) {
  idbDel(id).catch(() => {});
  write(read().filter((j) => j.id !== id));
}

/** Annulla un video ancora in coda o in invio (i video già caricati non sono in coda). */
export function cancelJob(id) {
  removeJob(id);
}

export function cancelAll() {
  read().filter((j) => j.status !== "done").forEach((j) => removeJob(j.id));
}

const patch = (id, fields) =>
  write(read().map((j) => (j.id === id ? { ...j, ...fields } : j)));

export async function processQueue() {
  if (running || !navigator.onLine) return;
  running = true;
  try {
    for (;;) {
      const job = read().find((j) => j.status !== "done" && j.status !== "error");
      if (!job) break;
      const blob = await idbGet(job.id).catch(() => null);
      if (!blob) {
        // Il file non è più nel telefono: segnaliamo invece di sparire in silenzio.
        patch(job.id, { status: "error", error: "Video non più disponibile sul telefono: registralo di nuovo" });
        continue;
      }
      const file = new File([blob], job.name, { type: job.type, lastModified: job.lastModified });
      patch(job.id, { status: "uploading", error: "" });
      try {
        const data = await uploadFileChunked(file, (pct, info) => patch(job.id, { pct, kbps: info?.kbps || null, etaSeconds: info?.etaSeconds ?? null }));
        if (job.kind === "proof") {
          await api.post(`/cleanings/${job.cleaningId}/proof`, {
            req_id: job.reqId,
            file_id: data.id,
            kind: job.proofKind || "foto",
            ...(job.unitId ? { unit_id: job.unitId } : {}),
          });
        } else {
          await api.post(`/cleanings/${job.cleaningId}/video`, {
            video_file_id: data.id,
            ...(job.unitId ? { unit_id: job.unitId } : {}),
          });
        }
        patch(job.id, { status: "done", pct: 100, fileId: data.id });
        removeJob(job.id);
      } catch (e) {
        const attempts = (job.attempts || 0) + 1;
        const msg = e?.response?.data?.detail || e?.message || "rete assente";
        patch(job.id, {
          attempts,
          status: attempts >= 8 ? "error" : "pending",
          error: typeof msg === "string" ? msg : "invio non riuscito",
        });
        break;    // riprova al prossimo evento di rete
      }
    }
  } finally {
    running = false;
  }
}

export function retryJob(id) {
  patch(id, { status: "pending", attempts: 0, error: "" });
  processQueue();
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => processQueue());
  setInterval(() => processQueue(), 30000);
  setTimeout(() => processQueue(), 2000);
}
